import type { ShopTabId } from "../ui/shops/shopsModel";
import { openShops } from "../ui/shops/shopsScreen";
import { ShopsLink, setShopsLink, shopsLink } from "../ui/shops/shopsLink";
import type { Feature, HubApi } from "./registry";
import "../ui/styles/shops.css";

/**
 * The currency shops in the hub (docs/SHOP.md §9.5): reached from the Shop tab (M35: Marks → Mystery Merchant and Event
 * shop, Gems → VIP shop and the Merchant's refresh; a red count on the tab while a featured Event slot is affordable —
 * a badge, never a pop-up or a push), and `openShops(tab)` for the VIP page's Shop button and Market → Merchant. Earned currencies (Marks — Valor merged into them on 2026-09-30 — and
 * gems however they came), so the shops show on the web without a store. The rules are @highmarch/app `Shops`
 * (LevelCatalog.shops); nothing here opens by itself.
 */

/** More-sheet slot: after the Market (20), before VIP (25). */
const SHOPS_ROW_ORDER = 22;

export { openShops };

export const shopsFeature: Feature = {
  id: "shops",
  register(ctx, registry) {
    const shops = () => ctx.catalog.shops;
    const link: ShopsLink = {
      catalog: ctx.catalog, get shops() { return ctx.catalog.shops; }, now: ctx.now, hub: null,
      changed: () => { if (link.hub) link.hub.changed(); else ctx.persist(); },
    };
    setShopsLink(link);
    const withHub = (hub: HubApi) => { link.hub = hub; return link; };
    // The button shows once a shop is open (the Event shop from level 3, the Merchant from level 5).
    const anyOpen = () => shops().unlocked("merchant") || shops().unlocked("event");
    const open = (hub: HubApi, tab: ShopTabId | null) => { withHub(hub); openShops(tab); };

    // M35: the Shops button folded into the Shop tab (Marks → Merchant and Event shop, Gems → VIP shop), and its
    // badge with it: a red count on the Shop tab while a featured Event slot is affordable.
    registry.tabBadge({ tab: "shop", count: () => (anyOpen() ? shops().badge(ctx.now()) + shops().merchantFree(ctx.now()) : 0) });
    registry.moreRow({
      id: "shops", order: SHOPS_ROW_ORDER, icon: "icons/shop_merchant.png", home: "shop",
      label: () => "Shops",
      note: () => [shops().unlocked("merchant") ? "Merchant" : "", shops().unlocked("event") ? "Event" : "", shops().unlocked("vip") ? "VIP" : ""].filter(Boolean).join(" · "),
      // no red count here: the Shop tab carries the one badge
      visible: anyOpen,
      open: (hub) => open(hub, null),
    });
    registry.hubWidget({
      id: "shops",
      mount: (hub) => { withHub(hub); return { dispose: () => { if (shopsLink()?.hub === hub) link.hub = null; } }; },
    });
    if (ctx.debug) {
      (window as unknown as { __tdShops: unknown }).__tdShops = {
        get shops() { return ctx.catalog.shops; }, link, open: (tab?: ShopTabId) => openShops(tab ?? null),
        /** Smoke tests and captures: earned currencies without playing. */
        grant: (currency: "marks" | "gems", n: number) => { ctx.catalog.economy.wallet.grant(currency, n, "debug", ctx.now()); link.changed(); },
      };
    }
  },
};
